import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface TelegramAccountsData {
  total_accounts: number;
  limited_accounts: number;
  active_accounts: number;
  is_nearly_empty: boolean;
  warning_message?: string;
  accounts_list: Array<{
    folder: string;
    session_files: number;
    has_json: boolean;
  }>;
  limited_list: Array<{
    folder: string;
    session_files: number;
    has_json: boolean;
  }>;
}

export function TelegramAccounts() {
  const [data, setData] = useState<TelegramAccountsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTelegramAccounts();
  }, []);

  const fetchTelegramAccounts = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/auth/admin/telegram_accounts', {
        credentials: 'include',
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.ok) {
          setData(result.data);
        } else {
          console.error('API error:', result.error);
        }
      } else {
        console.error('HTTP error:', response.status);
      }
    } catch (error) {
      console.error('Error fetching telegram accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Telegram аккаунты</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p>Загрузка данных об аккаунтах...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Общая статистика */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Всего аккаунтов</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data?.total_accounts || 0}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Активных</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{data?.active_accounts || 0}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">В лимите</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{data?.limited_accounts || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Предупреждение */}
      {data?.is_nearly_empty && data.warning_message && (
        <Card className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20">
          <CardHeader>
            <CardTitle className="text-yellow-800 dark:text-yellow-200">
              Предупреждение
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-yellow-700 dark:text-yellow-300">
              {data.warning_message}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Активные аккаунты */}
      <Card>
        <CardHeader>
          <CardTitle>Активные аккаунты</CardTitle>
          <CardDescription>
            Список работающих Telegram аккаунтов
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {data?.accounts_list.length === 0 ? (
              <p className="text-sm text-muted-foreground">Нет активных аккаунтов</p>
            ) : (
              <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                {data?.accounts_list.map((account) => (
                  <div key={account.folder} className="p-3 border rounded-lg">
                    <div className="font-medium">{account.folder}</div>
                    <div className="text-sm text-muted-foreground">
                      Session файлов: {account.session_files}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      JSON конфиг: {account.has_json ? '✅' : '❌'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Аккаунты в лимите */}
      {data?.limited_list && data.limited_list.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Аккаунты в лимите</CardTitle>
            <CardDescription>
              Заблокированные или ограниченные аккаунты
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                {data.limited_list.map((account) => (
                  <div key={account.folder} className="p-3 border rounded-lg bg-red-50 dark:bg-red-900/20">
                    <div className="font-medium text-red-800 dark:text-red-200">
                      {account.folder}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Session файлов: {account.session_files}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      JSON конфиг: {account.has_json ? '✅' : '❌'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Управление */}
      <Card>
        <CardHeader>
          <CardTitle>Управление аккаунтами</CardTitle>
          <CardDescription>
            Действия с Telegram аккаунтами
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-4">
            <Button onClick={fetchTelegramAccounts} disabled={loading}>
              {loading ? 'Обновление...' : 'Обновить данные'}
            </Button>
            <Button variant="outline" disabled>
              Добавить аккаунт
            </Button>
            <Button variant="outline" disabled>
              Проверить статус
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            Дополнительные функции управления аккаунтами будут добавлены позже
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
